
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormModal, Select, useToastActions } from '@airclou/ui';
import { updateCompanyMember } from '@/api/groups';
import type { CompanyMember } from '@/api/groups';

interface EditMemberModalProps {
  companyId: string;
  member: CompanyMember;
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
}

export function EditMemberModal({ companyId, member, isOpen, onClose, onSuccess }: EditMemberModalProps) {
  const { t } = useTranslation('dashboard');
  const { success, error: showError } = useToastActions();
  const [loading, setLoading] = useState(false);
  const [role, setRole] = useState(member.company_role);

  const handleSubmit = async () => {
    setLoading(true);
    try {
      await updateCompanyMember(companyId, member.user_id, {
        role: role,
      });
      success(t('people.editMember'));
      onSuccess();
    } catch (err: any) {
      showError(err.message || t('people.failedToRemove'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <FormModal
      isOpen={isOpen}
      onClose={onClose}
      title={t('people.editMember')}
      size="md"
      submitText={loading ? t('common.saving') : t('common.save')}
      canSubmit={!loading}
      isLoading={loading}
      onSubmit={handleSubmit}
    >
      <div className="space-y-6">
        <div>
          <label htmlFor="member-name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('common.name')}
          </label>
          <div className="px-3 py-2 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-md text-gray-700 dark:text-gray-300">
            {member.name}
          </div>
        </div>

        <div>
          <label htmlFor="member-email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('common.email')}
          </label>
          <div className="px-3 py-2 border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800 rounded-md text-gray-700 dark:text-gray-300">
            {member.email}
          </div>
        </div>

        <div>
          <label htmlFor="member-role" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            {t('common.role')}
          </label>
          <Select
            value={role}
            onChange={(val) => setRole(val as any)}
            options={[
              { value: 'admin', label: t('common.admin') },
              { value: 'member', label: t('common.member') },
            ]}
          />
          <p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
            {t('workspaceTabs.adminsManage')}
          </p>
        </div>
      </div>
    </FormModal>
  );
}
